/**
 * Filter Values module
 * Provides global getFilterValues function that works with FilterManager
 */

import { CONFIG } from '../../core/config.js';
import { logError } from '../../core/error-handler.js';
import { ObjectUtils, SessionStorageUtils, FormUtils } from '../../core/shared-utilities.js';
import { FilterStorage } from './session-storage.js';

/**
 * Global getFilterValues function that automatically works with FilterManager
 * This function can be used in any view that has FilterManager initialized
 * It will automatically collect values from all detected filter components
 *
 * @returns {Object} Object containing all filter values for the current page
 *
 * @example
 * // In Kendo Grid data source configuration:
 * .Read(r => r.Action("GetData", "Controller").Data("getFilterValues"))
 *
 * @description This function works by:
 * 1. Finding the active FilterManager instance on the page
 * 2. Using its generateFilterValues() method to collect all filter values
 * 3. Returning the values in the format expected by Kendo Grid data source
 */
/**
 * Expand array values to indexed format for ASP.NET Core model binding
 * e.g., {SeasonIds: [1, 2]} becomes {'SeasonIds[0]': 1, 'SeasonIds[1]': 2}
 * @param {Object} values - Filter values object
 * @returns {Object} Expanded filter values
 */
function expandArrayValues(values) {
    if (!values || typeof values !== 'object') {
        return values;
    }

    const expanded = {};

    Object.keys(values).forEach(key => {
        const value = values[key];
        if (Array.isArray(value)) {
            value.forEach((item, index) => {
                expanded[`${key}[${index}]`] = item;
            });
        } else {
            expanded[key] = value;
        }
    });

    return expanded;
}

function getFilterValues() {
    try {
        // First, try to find FilterManager instance from the grid's data source
        const grids = $(CONFIG.FILTER_VALUES.GRID_SELECTOR);

        for (let i = 0; i < grids.length; i++) {
            const grid = $(grids[i]).data('kendoGrid');
            if (grid && grid._filterManager && !grid._filterManager.isDestroyed) {
                const values = grid._filterManager.generateFilterValues();

                // Pokud FilterManager nevrátil žádné hodnoty, zkus načíst ze session storage
                return SessionStorageUtils.getFallbackValues(values, () => {
                    if (typeof FilterStorage !== 'undefined' && FilterStorage.isAvailable()) {
                        return expandArrayValues(FilterStorage.load());
                    }
                    return {};
                });
            }
        }

        // If no grid-specific FilterManager found, try to find any FilterManager on the page
        if (window._activeFilterManager && !window._activeFilterManager.isDestroyed) {
            const values = window._activeFilterManager.generateFilterValues();

            // Pokud globální FilterManager nevrátil žádné hodnoty, zkus načíst ze session storage
            return SessionStorageUtils.getFallbackValues(values, () => {
                if (typeof FilterStorage !== 'undefined' && FilterStorage.isAvailable()) {
                    return expandArrayValues(FilterStorage.load());
                }
                return {};
            });
        }

        // Fallback: try to load from session storage directly
        if (FilterStorage.isAvailable()) {
            const savedValues = FilterStorage.load();
            if (ObjectUtils.hasProperties(savedValues)) {
                return expandArrayValues(savedValues);
            }
        }

        // If no FilterManager found and no saved values, return empty object
        return {};
    } catch (error) {
        logError(error);
        return {};
    }
}

/**
 * Initialize the filter values module
 * This function sets up the global getFilterValues function
 */
function initializeFilterValues() {
    // Make getFilterValues available globally
    window.getFilterValues = getFilterValues;
}

/**
 * Apply values to form elements (based on Scio.Externists approach)
 * @param {Object} values - Values to apply
 */
function applyValuesToForm(values) {
    return FormUtils.applyValuesToForm(values);
}

/**
 * Extract filter values from URL query parameters (for cross-page filtering).
 * Only includes parameters whose keys match an element ID inside the #filter form.
 * Handles repeated parameters as arrays (e.g., SeasonTermTypeIds=3&SeasonTermTypeIds=4).
 *
 * @returns {Object|null} Filter values object, or null if no matching params found
 */
function extractUrlFilterValues() {
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.size === 0) return null;

        const filterForm = document.querySelector(CONFIG.SELECTORS.FILTER_FORM);
        if (!filterForm) return null;

        const values = {};

        for (const [key, value] of params) {
            // Only process params that match a filter form element ID
            const element = filterForm.querySelector(`#${CSS.escape(key)}`);
            if (!element) continue;

            // Handle repeated params as arrays (e.g., SeasonTermTypeIds=3&SeasonTermTypeIds=4)
            if (key in values) {
                if (!Array.isArray(values[key])) {
                    values[key] = [values[key]];
                }
                values[key].push(value);
            } else {
                // Check if the element is a multi-select (select[multiple]) - always use array
                const isMultiSelect = element.tagName === 'SELECT' && element.multiple;
                values[key] = isMultiSelect ? [value] : value;
            }
        }

        return Object.keys(values).length > 0 ? values : null;
    } catch (error) {
        logError(error);
        return null;
    }
}

/**
 * Pre-load filter values from URL parameters or session storage and apply them immediately.
 * URL parameters take priority over session storage (used for cross-page filtering).
 * This function should be called before Kendo UI initialization.
 * It only runs once per page load to prevent re-applying cleared values.
 */
function preloadFilterValues() {
    // Check URL parameters first (cross-page filtering, e.g., from schedule wizard)
    const urlValues = extractUrlFilterValues();
    if (urlValues) {
        // Save to session storage so FilterManager phase 2 picks them up via Kendo widget API
        if (typeof FilterStorage !== 'undefined' && FilterStorage.isAvailable()) {
            FilterStorage.save(urlValues);
        }
        applyValuesToForm(urlValues);
        // Clean URL to prevent re-triggering on refresh
        if (window.history && window.history.replaceState) {
            window.history.replaceState(null, '', window.location.pathname);
        }
        return true;
    }

    // Fall back to session storage
    if (typeof FilterStorage !== 'undefined' && FilterStorage.isAvailable()) {
        const savedValues = FilterStorage.load();
        if (savedValues && Object.keys(savedValues).length > 0) {
            // Apply values immediately to form elements
            applyValuesToForm(savedValues);
            return true;
        }
    }

    return false;
}

// Export for module system
export { getFilterValues, initializeFilterValues, preloadFilterValues };

// Auto-initialize when module is loaded
initializeFilterValues();
